import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoiceDollar, faHandHoldingUsd, faCalculator, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { useGetActiveLoanProductsQuery, useGetLoanProductByIdQuery } from "../../services/loanProductApiSlice";
import { useAddLoanApplicationMutation } from "../../services/loanApplicationApiSlice";
import { useAuth } from "../../context/AuthContext";
import { AlertCircle, CheckCircle, ChevronRight, Banknote, Calendar, FileText, Sparkles } from 'lucide-react';

const ApplyLoan = () => {
  const { user } = useAuth();
  const { data: loanProducts = [] } = useGetActiveLoanProductsQuery();
  const [addLoanApplication, { isLoading: isAdding }] = useAddLoanApplicationMutation();

  const [loan, setLoan] = useState({
    loanProductId: "",
    amount: "",
    term: "",
    interestRate: "",
    reason: ""
  });

  // Fetch loan product details when loanProductId changes
  const { data: loanProductDetails, isLoading: isLoadingProductDetails } = useGetLoanProductByIdQuery(
    loan.loanProductId,
    { skip: !loan.loanProductId || loan.loanProductId === "" }
  );

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Calculate estimated monthly payment
  const estimatedMonthlyPayment = loan.amount && loan.term 
    ? (Number(loan.amount) / Number(loan.term)).toFixed(2)
    : "0.00";

  useEffect(() => {
    if (loanProducts && loanProducts.length > 0) {
      const firstProduct = loanProducts[0];
      setLoan((prev) => ({ ...prev, loanProductId: firstProduct.loanProductId }));
      
      console.log("First product is , "+JSON.stringify(firstProduct));
      // Auto-fill interestRate and term from first loan product
      if (firstProduct.interestRate !== undefined) {
        setLoan((prev) => ({ ...prev, interestRate: String(firstProduct.interestRate) }));
      }
      if (firstProduct.defaultTermMonths !== undefined) {
        setLoan((prev) => ({ ...prev, term: String(firstProduct.defaultTermMonths) }));
      }
    }
  }, [loanProducts]);

  // Auto-fill interestRate and defaultTermMonths when loan product details are fetched
  useEffect(() => {
    if (loanProductDetails) {
      console.log("Loan product details fetched:", loanProductDetails);
      // Extract interestRate from response (handle ResultSet format)
      const productData = loanProductDetails.ResultSet || loanProductDetails;
      
      console.log("Product data:", productData[0]);
      
      if (productData && (productData[0].interestRate !== undefined || productData[0].defaultTermMonths !== undefined)) {
        setLoan((prev) => ({
          ...prev,
          interestRate: productData[0].interestRate !== undefined ? String(productData[0].interestRate) : prev.interestRate,
          term: productData[0].defaultTermMonths !== undefined ? String(productData[0].defaultTermMonths) : prev.term
        }));
      }
    }
  }, [loanProductDetails]);

  const validateForm = () => {
    const newErrors = {};
    if (!loan.loanProductId) {
      newErrors.loanProductId = 'Please select a loan product';
    }
    if (!loan.amount || isNaN(Number(loan.amount)) || Number(loan.amount) <= 10000) {
      newErrors.amount = 'Loan amount must be greater than 10,000';
    }
    if (!loan.term || isNaN(Number(loan.term)) || Number(loan.term) <= 0) {
      newErrors.term = 'Please enter a valid term';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoan({ ...loan, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert("Please log in to submit a loan application.");
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const borrowerId = user?.borrowerId || user?.id || user?.userId || null;

    if (!borrowerId) {
      alert("Your account is missing a borrower ID. Please contact support or ensure you're logged in as a borrower.");
      return;
    }

    const payload = {
      borrowerId,
      loanProductId: Number(loan.loanProductId),
      requestedAmount: Number(loan.amount),
      requestedTermMonths: Number(loan.term),
      interestRate: loan.interestRate ? parseFloat(loan.interestRate) : null,
      status: "Pending",
      reason: loan.reason || null,
    };

    console.log("Submitting loan application payload:", payload);

    try {
      const response = await addLoanApplication(payload).unwrap();
      console.log("AddLoanApplication response:", response);
      const message = response?.Result || response?.Message || response?.result || "Loan application submitted successfully.";
      
      // Show success state
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setLoan({ 
          loanProductId: loanProducts[0]?.loanProductId || "", 
          amount: "", 
          term: "", 
          interestRate: "",
          reason: "" 
        });
        setErrors({});
      }, 3000);
      
    } catch (err) {
      console.error("AddLoanApplication error:", err);

      // Try to extract useful server error messages commonly returned by backend
      const serverMsg = err?.data?.Result || err?.data?.Message || err?.error || err?.data || err?.message;
      const displayMsg = typeof serverMsg === "string" ? serverMsg : JSON.stringify(serverMsg);

      alert(displayMsg || "Failed to submit loan application. Please try again.");
    }
  };

  const handleClear = () => {
    setLoan({ 
      loanProductId: loanProducts[0]?.loanProductId || "", 
      amount: "", 
      term: "", 
      interestRate: "",
      reason: "" 
    });
    setErrors({});
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl mb-6 shadow-lg">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Application Submitted!</h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Your loan application has been received. Our team will review it within 2-3 business days.
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl border border-green-200 p-10 shadow-xl max-w-3xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-8">
              <div className="md:w-2/3">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-2xl text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Application Summary</h2>
                    <p className="text-gray-600">Here's what you applied for</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Banknote className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Loan Amount</p>
                        <p className="text-2xl font-bold text-gray-900">${loan.amount}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Loan Term</p>
                        <p className="text-2xl font-bold text-gray-900">{loan.term} months</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Purpose</p>
                        <p className="text-lg font-semibold text-gray-900">{loan.reason || "Not specified"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="md:w-1/3 bg-gradient-to-b from-green-100 to-white rounded-2xl p-8 shadow-inner">
                <h3 className="font-bold text-gray-900 mb-6 text-lg">Next Steps</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <p className="text-gray-700">Document verification</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <p className="text-gray-700">Credit check</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <p className="text-gray-700">Final approval</p>
                  </div>
                </div>
                <div className="mt-8 p-4 bg-white rounded-xl border border-green-200">
                  <p className="text-sm text-gray-600 mb-2">Estimated monthly payment</p>
                  <p className="text-3xl font-bold text-gray-900">${estimatedMonthlyPayment}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl mb-6 shadow-xl">
            <FontAwesomeIcon icon={faHandHoldingUsd} className="text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
            Apply for a Loan
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get the financial support you need with our competitive rates and flexible terms
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Side - Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-2xl relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-50 rounded-full translate-y-12 -translate-x-12"></div>
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-xl text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Loan Application Form</h2>
                    <p className="text-gray-600">Fill in the details below</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Loan Product */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FontAwesomeIcon icon={faHandHoldingUsd} className="text-blue-600" />
                      Select Loan Product
                    </label>
                    <div className="relative">
                      <select
                        name="loanProductId"
                        value={loan.loanProductId}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 hover:border-gray-300 group-hover:shadow-sm appearance-none"
                      >
                        {loanProducts && loanProducts.length > 0 ? (
                          loanProducts.map((p) => (
                            <option key={p.loanProductId} value={p.loanProductId}>
                              {p.loanProductName}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="">Select a product</option>
                            <option value="0">Personal Loan</option>
                            <option value="1">Car Loan</option>
                            <option value="2">Business Loan</option>
                            <option value="3">Education Loan</option>
                          </>
                        )}
                      </select>
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                        <ChevronRight className="w-5 h-5 rotate-90" />
                      </div>
                    </div>
                    {errors.loanProductId && (
                      <div className="flex items-center gap-2 mt-3 text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-100">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {errors.loanProductId}
                      </div>
                    )}
                  </div>

                  {/* Amount, Term and Interest Grid */}
                  <div className="grid md:grid-cols-3 gap-8">
                    {/* Amount */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalculator} className="text-blue-600" />
                        Loan Amount
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <Banknote className="w-5 h-5" />
                        </div>
                        <input
                          type="number"
                          name="amount"
                          value={loan.amount}
                          onChange={handleChange}
                          placeholder="10,000"
                          className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 hover:border-gray-300 group-hover:shadow-sm"
                        />
                      </div>
                      {errors.amount && (
                        <div className="flex items-center gap-2 mt-3 text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-100">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          {errors.amount}
                        </div>
                      )}
                    </div>

                    {/* Term */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
                        Loan Term (Months)
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <input
                          type="number"
                          name="term"
                          disabled="true"
                          value={loan.term}
                          onChange={handleChange}
                          placeholder="24"
                          className="w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 hover:border-gray-300 group-hover:shadow-sm"
                        />
                      </div>
                      {errors.term && (
                        <div className="flex items-center gap-2 mt-3 text-red-600 text-sm bg-red-50 p-3 rounded-xl border border-red-100">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          {errors.term}
                        </div>
                      )}
                    </div>

                    {/* Interest Rate */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FontAwesomeIcon icon={faCalculator} className="text-blue-600" />
                        Interest Rate (%)
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                          {isLoadingProductDetails ? (
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                          ) : (
                            <span className="text-sm">%</span>
                          )}
                        </div>
                        <input
                          type="number"
                          name="interestRate"
                          value={loan.interestRate}
                          onChange={handleChange}
                          placeholder={isLoadingProductDetails ? "Loading..." : "5.0"}
                          disabled='true'
                          className={`w-full pl-12 pr-5 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 hover:border-gray-300 group-hover:shadow-sm ${isLoadingProductDetails ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      {isLoadingProductDetails && (
                        <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading interest rate...
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Reason for Loan
                    </label>
                    <textarea
                      name="reason"
                      value={loan.reason}
                      onChange={handleChange}
                      placeholder="Please describe the purpose of this loan (optional)"
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 text-gray-900 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 hover:border-gray-300 group-hover:shadow-sm resize-none min-h-[120px]"
                    />
                  </div>

                  {/* Info Box */}
                  <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-blue-800 font-medium mb-1">Application Note</p>
                        <p className="text-sm text-blue-700">
                          Your application will be reviewed within 2-3 business days. Interest rates are determined based on your creditworthiness and the selected loan product.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={isAdding}
                      className={`flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                        isAdding ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {isAdding ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Processing...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          Submit Application
                          <ChevronRight className="w-5 h-5" />
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleClear}
                      className="px-8 py-4 bg-white text-gray-700 font-medium rounded-2xl border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 shadow-sm hover:shadow"
                    >
                      Clear Form
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Right Side - Info Panel */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-b from-blue-50 to-white rounded-3xl border border-blue-100 p-8 shadow-xl h-full">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Loan Calculator</h3>
                
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">Loan Amount</span>
                    <span className="text-2xl font-bold text-gray-900">${loan.amount || "0"}</span>
                  </div>
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600">Term</span>
                    <span className="text-xl font-semibold text-gray-900">{loan.term || "0"} months</span>
                  </div>
                  <div className="h-px bg-gray-200 my-4"></div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Estimated Monthly</span>
                    <span className="text-3xl font-bold text-blue-600">${estimatedMonthlyPayment}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Why Choose Us?</h4>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">Competitive interest rates</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">Flexible repayment terms</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">Quick approval process</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">No hidden fees</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-3">Need Help?</h4>
                <p className="text-sm text-gray-700 mb-4">
                  Our loan specialists are available to assist you with your application.
                </p>
                <button className="w-full py-3 bg-white text-blue-600 font-medium rounded-xl border border-blue-200 hover:bg-blue-50 transition-colors">
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyLoan;